import { useState, useEffect, useContext } from "react";
import { Modal } from "antd";
import { createDatasource } from "@ts/retrieve-data";
import { mainContext } from "@ts/constants";
import * as Sentry from "@sentry/react";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import {
    IChannel,
    IMainContext,
    TCommonRowData,
    TTimezone,
} from "@ts/common-types";
import { useSelector } from "react-redux";
import { ParseDateCellRenderer } from "../cell-renderers/AllCellRenderers";
import { titleCase } from "@ts/utils";
import { Tabs } from "antd";
import type { TabsProps } from "antd";
import AgGridModalLayout from "../wrappers/layouts/AgGridModalLayout";
import {
    FaCheckCircle,
    FaGlobeAmericas,
    FaCalendarAlt,
    FaBan,
} from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";

const RequestModal = () => {
    const { requestModalDisp, setRequestModalDisp } =
        useContext<IMainContext>(mainContext);
    const [rowData, setRowData] = useState<any[]>();
    const [gridHeight, setGridHeight] = useState<number>();
    const channel = useSelector((state: IChannel) => state.channel.value);
    const clientTimezone = useSelector(
        (state: Record<string, TTimezone>) => state.clientTimezone.value
    );
    const [columnDefs] = useState<ColDef[]>([
        {
            field: "id",
            headerName: "Request ID",
            filter: "agNumberColumnFilter",
        },
        {
            field: "module",
            headerName: "Module",
            filter: "agTextColumnFilter",
            valueFormatter: (params: ValueFormatterParams) =>
                params.value && titleCase(params.value),
        },
        {
            field: "action",
            headerName: "Action",
            filter: true,
        },
        {
            field: "no_of_items",
            headerName: "# Items",
            filter: "agNumberColumnFilter",
        },
        {
            field: "requested_at",
            headerName: "Requested At",
            filter: true,
            cellRenderer: ParseDateCellRenderer,
        },
        {
            field: "requested_by",
            headerName: "Requested By",
            filter: true,
        },
        {
            field: "response_time",
            headerName: "Response Time",
            filter: true,
            cellRenderer: ParseDateCellRenderer,
        },
    ]);

    const handleClose = () => {
        setRequestModalDisp(false);
    };

    const populateClientRequestsModal = async () => {
        const data = await createDatasource("client-requests", {});
        return data;
    };

    useEffect(() => {
        if (requestModalDisp) {
            populateClientRequestsModal()
                .then((clientRequests) => {
                    setRowData(clientRequests as TCommonRowData);
                })
                .catch((err) => {
                    console.error(err);
                    Sentry.captureException(err);
                });
        }
    }, [requestModalDisp, channel]);

    const ChildTable = ({ label }: { label: string }) => {
        const columns =
            label === "pending"
                ? columnDefs.filter((data) => data.field !== "response_time")
                : columnDefs;

        return (
            <div
                id="rules-change-logs-grid"
                className="ag-theme-alpine"
                style={{ height: `${gridHeight - 50}px`, margin: 0 }}
            >
                <AgGridModalLayout
                    rowData={
                        rowData &&
                        rowData.filter(
                            (data) => data.status.toLowerCase() === label
                        )
                    }
                    columnDefs={columns}
                    setGridHeight={setGridHeight}
                />
            </div>
        );
    };

    const TabLabel = ({
        label,
        color,
        Icon,
    }: {
        label: string;
        color: string;
        Icon: any;
    }) => {
        return (
            <div className={`text-${color} d-flex align-items-center gap-1`}>
                <Icon />
                {label}
            </div>
        );
    };

    const items: TabsProps["items"] = [
        {
            key: "Pending",
            label: (
                <TabLabel
                    label="Pending"
                    color="primary"
                    Icon={FaCalendarAlt}
                />
            ),
            children: <ChildTable label="pending" />,
        },
        {
            key: "Failed",
            label: <TabLabel label="Failed" color="danger" Icon={FaBan} />,
            children: <ChildTable label="fail" />,
        },
        {
            key: "Success",
            label: (
                <TabLabel
                    label="Success"
                    color="success"
                    Icon={FaCheckCircle}
                />
            ),
            children: <ChildTable label="success" />,
        },
    ];

    return (
        <>
            <Modal
                title={
                    <div className="d-flex align-items-center">
                        <h4 className="mb-0">Request History</h4>
                        <div
                            className="text-secondary mx-3"
                            style={{ fontSize: "0.9rem" }}
                        >
                            <FaGlobeAmericas className="mx-1 mb-1" />
                            <span style={{ fontSize: "1rem" }}>
                                {clientTimezone}
                            </span>
                        </div>
                    </div>
                }
                open={requestModalDisp}
                onCancel={handleClose}
                width="95%"
                style={{ top: 20, height: "95vh" }}
                styles={{ body: { height: "82vh", overflowY: "auto" } }}
                closeIcon={<AiOutlineClose style={{ fontSize: "25px" }} />}
                footer={null}
            >
                <div className="request-modal-body">
                    <Tabs
                        type="card"
                        className="my-2"
                        defaultActiveKey="1"
                        items={items}
                    />
                </div>
            </Modal>
        </>
    );
};

export default RequestModal;
